import ko from "knockout";
import _ from "lodash";
import Promise from "bluebird";
import moment from "moment";
import qualityManagementService from "services/qualityManagementService";
import qualityAuth from "services/qualityAuth";
import errorCallbackFactory from "util/errorCallbackFactory";
import ViewModel from "viewModels/ViewModel";
import toastService from "services/toastService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
import {
    SHORT_WEEKDAY_FULL_DATETIME_OPTIONS,
    SHORT_DATE_OPTIONS,
} from "util/DateTimeHelper";

const RecordingRestoreDetailsView = ViewModel.extend(function (
    recordingObservable,
    options = {},
    conversation,
    conversationOriginatingDirection
) {
    const self = this;
    self.afterRender = function () {};
    self.header = ko.computed(function () {
        return false;
    });
    self.isSaving = ko.observable(false);
    self.recording = recordingObservable;
    self.restorePeriod = null;
    self.canRestore = self.computed(function () {
        if (!recordingObservable()) {
            return false;
        }

        const recording = recordingObservable();

        if (recording.media === "screen") {
            return qualityAuth.canRestoreScreenRecordings();
        } else if (
            recording.media === "audio" &&
            recording.mediaSubtype === "Snippet"
        ) {
            return qualityAuth.canRestoreSnippetRecording();
        } else {
            return qualityAuth.canRestoreRecording();
        }
    });
    const readOnlyObservableFalse = self.computed(function () {
        return false;
    });

    self.conversationOriginatingDirection = ko.isObservable(
        conversationOriginatingDirection
    )
        ? conversationOriginatingDirection
        : ko.observable(conversationOriginatingDirection);

    self.periods = [
        {
            id: 1,
            name: ko.i18n.renderString("playback.archivePeriods.1day"),
        },
        {
            id: 7,
            name: ko.i18n.renderString("playback.archivePeriods.7days"),
        },
        {
            id: 30,
            name: ko.i18n.renderString("playback.archivePeriods.30days"),
        },
    ];

    const FILESTATE_ARCHIVED = "ARCHIVED";
    const FILESTATE_RESTORING = "RESTORING";
    const FILESTATE_RESTORED = "RESTORED";
    const FILESTATE_UPLOADING = "UPLOADING";
    const FILESTATE_DELETED = "DELETED";

    const timeFormatWithSeconds = ko.i18n.renderString(
        "interactionsList.timeFormatSecondsShort"
    );

    self.conversation = ko.isObservable(conversation)
        ? conversation
        : ko.observable(conversation);

    self.isEmail = self.computed(function () {
        return (
            self.recording() && ko.unwrap(self.recording().media) === "email"
        );
    });

    //Calculates startTime only for email interactions
    self.displayStartTime = self.computed(function () {
        if (self.conversation() && ko.unwrap(self.conversation().startTime)) {
            return ko.i18n.formatDate(
                ko.unwrap(self.conversation().startTime),
                timeFormatWithSeconds,
                null,
                SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
            );
        } else if (
            self.recording() &&
            self.recording().startTime &&
            self.isEmail()
        ) {
            return ko.i18n.formatDate(
                ko.unwrap(self.recording().startTime),
                timeFormatWithSeconds,
                null,
                SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
            );
        }
    });

    self.startTime = self.computed(function () {
        if (self.conversation() && ko.unwrap(self.conversation().startTime)) {
            return self.conversation().startTime;
        } else if (self.recording() && ko.unwrap(self.recording().startTime)) {
            return self.recording().startTime;
        }
    });

    //Calculates endTime only for email interactions
    self.displayEndTime = self.computed(function () {
        if (self.conversation() && ko.unwrap(self.conversation().endTime)) {
            return ko.i18n.formatDate(
                ko.unwrap(self.conversation().endTime),
                timeFormatWithSeconds,
                null,
                SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
            );
        } else if (
            self.recording() &&
            self.recording().endTime &&
            self.isEmail()
        ) {
            return ko.i18n.formatDate(
                ko.unwrap(self.recording().endTime),
                timeFormatWithSeconds,
                null,
                SHORT_WEEKDAY_FULL_DATETIME_OPTIONS
            );
        }
    });

    self.selectedPeriod = ko.observable();

    self.isArchived = self.computed(function () {
        return (
            self.recording() &&
            self.recording().fileState &&
            ko.unwrap(self.recording().fileState) === FILESTATE_ARCHIVED
        );
    });

    self.isRestoring = self.computed(function () {
        return (
            self.recording() &&
            self.recording().fileState &&
            ko.unwrap(self.recording().fileState) === FILESTATE_RESTORING
        );
    });

    self.isRestored = self.computed(function () {
        return (
            self.recording() &&
            self.recording().fileState &&
            ko.unwrap(self.recording().fileState) === FILESTATE_RESTORED
        );
    });

    self.isUploading = self.computed(function () {
        return (
            self.recording() &&
            self.recording().fileState &&
            ko.unwrap(self.recording().fileState) === FILESTATE_UPLOADING
        );
    });

    self.isDeleted = self.computed(function () {
        return (
            (self.recording() &&
                self.recording().fileState &&
                ko.unwrap(self.recording().fileState) === FILESTATE_DELETED) ||
            !self.recording() ||
            !self.recording().fileState
        );
    });

    self.orgRestoresRemaining = self.computed(function () {
        if (
            self.recording() &&
            self.recording().remainingRestorationsAllowedForOrg
        ) {
            return ko.unwrap(
                self.recording().remainingRestorationsAllowedForOrg
            );
        }
        return 0;
    });

    self.canSetRestoreDays = self.computed(function () {
        return (
            (featureToggleService.isEnabled(
                FeatureToggle.WEM_1308_REMOVE_ARCHIVAL_RESTORATIONS_LIMIT
            ) ||
                self.orgRestoresRemaining() > 0) &&
            (self.isRestored() || self.isArchived())
        );
    });

    self.archivedDescriptionKey = self.computed(() => {
        if (
            featureToggleService.isEnabled(
                FeatureToggle.WEM_1308_REMOVE_ARCHIVAL_RESTORATIONS_LIMIT
            )
        ) {
            return "playback.recordingIsArchived.description.noRestoresCount";
        }

        return self.canSetRestoreDays()
            ? "playback.recordingIsArchived.description"
            : "playback.recordingIsArchived.description.noRestoresAvailable";
    });

    self.setRestoreDays = function (restorePeriod) {
        if (self.isSaving()) {
            return;
        }

        const recording = ko.toJS(self.recording);
        if (recording) {
            const opts = {};
            recording.archiveDate = moment()
                .add(restorePeriod, "day")
                .toISOString();
            self.isSaving(true);
            recording.fileState = FILESTATE_RESTORING;
            qualityManagementService
                .updateRecording(recording, opts)
                .then(function (result) {
                    //updateRecording results in call to backend playback service with a transcode type of "None" so
                    //media Uris are not generated, copy them from the original before updating
                    if (self.recording().mediaUris && !result.mediaUris) {
                        result.mediaUris = self.recording().mediaUris;
                    }
                    if (self.recording().mediaUri && !result.mediaUri) {
                        result.mediaUri = self.recording().mediaUri;
                    }
                    self.recording(result);
                })
                .catch(function (error) {
                    if (
                        error &&
                        error.details &&
                        error.details.status === 400 &&
                        error.details.response &&
                        error.details.response.code ===
                            "recording.not.archivable"
                    ) {
                        const errorKey =
                            "playback.archive.restoreRequest.400.recording.not.archivable";

                        // Example date string from api error details: Fri Jan 22 01:18:49 UTC 2021
                        const dateFormat = "ddd MMM DD hh:mm:ss [UTC] YYYY";
                        const archiveDate = moment.utc(
                            error.details.response.messageParams.archiveDate,
                            dateFormat
                        );
                        const deleteDate = moment.utc(
                            error.details.response.messageParams.deleteDate,
                            dateFormat
                        );
                        const errorMessage = ko.i18n.renderString(errorKey, {
                            archiveDate: ko.i18n.formatDate(
                                archiveDate,
                                null,
                                null,
                                SHORT_DATE_OPTIONS
                            ),
                            deleteDate: ko.i18n.formatDate(
                                deleteDate,
                                null,
                                null,
                                SHORT_DATE_OPTIONS
                            ),
                        });
                        toastService.notifyMajorAlert(errorMessage);
                    } else {
                        throw error;
                    }
                })
                .catch(
                    errorCallbackFactory.createCallback({
                        namespace: "playback.archive.restoreRequest",
                    })
                )
                .finally(function () {
                    self.isSaving(false);
                });
        }
    };

    self.restoreExpireDate = self.computed(function () {
        if (
            self.recording() &&
            self.isRestored() &&
            self.recording().restoreExpirationTime
        ) {
            return ko.unwrap(self.recording().restoreExpirationTime);
        }
    });

    self.subscribe(self.selectedPeriod, function (newValue) {
        if (newValue && newValue.id != self.restorePeriod) {
            self.restorePeriod = newValue.id;
            self.setRestoreDays(newValue.id);
        }
    });

    self.daysLeft = self.computed(function () {
        const expireDate = self.restoreExpireDate();
        if (expireDate) {
            return Math.round(moment(expireDate).diff(moment(), "days", true));
        }
        return 0;
    });

    self.searchPeriods = function (searchTerm) {
        let filteredPeriods = self.periods;
        if (searchTerm.length > 0) {
            filteredPeriods = _.filter(self.periods, function (period) {
                return (
                    period.name
                        .toLowerCase()
                        .indexOf(searchTerm.toLowerCase()) >= 0
                );
            });
        }
        return Promise.resolve(filteredPeriods);
    };

    self.handleRestorePeriodChange = function (_data, event) {
        const restorePeriod = parseInt(event?.target?.value);
        if (!isNaN(restorePeriod)) {
            self.setRestoreDays(restorePeriod);
        }
    };

    self.isAudio = self.computed(function () {
        return (
            self.recording() && ko.unwrap(self.recording().media) === "audio"
        );
    });

    self.isChat = self.computed(function () {
        return self.recording() && ko.unwrap(self.recording().media) === "chat";
    });

    self.isScreenRecording = self.computed(function () {
        return (
            self.recording() && ko.unwrap(self.recording().media) === "screen"
        );
    });

    self.isVoicemail = self.computed(function () {
        return (
            self.recording() &&
            ko.unwrap(self.recording().media) === "voicemail"
        );
    });

    self.isEmailOutbound = self.computed(function () {
        return self.conversationOriginatingDirection() === "outbound";
    });

    self.isEmailContainer = readOnlyObservableFalse;

    self.isMessagesContainer = readOnlyObservableFalse;

    Object.assign(self, options);
    self.recordingTypeHeaderInfo = ko.computed(function () {
        if (self.isAudio()) return { icon: "call", title: "audio" };
        if (self.isChat()) return { icon: "chat", title: "chat" };
        if (self.isScreenRecording())
            return { icon: "screen", title: "screen" };
        if (self.isVoicemail()) return { icon: "call", title: "voicemail" };
        if (self.isEmailContainer()) return { icon: "email", title: "email" };
        if (self.isMessagesContainer())
            return { icon: "message", title: "message" };
        else return false;
    });

    self.hasRestoreHeader = ko.computed(function () {
        return self.isRestored() && self.canRestore();
    });
});

export default RecordingRestoreDetailsView;
